import { useMemo, useState } from 'react';
import { ArrowsClockwise, CheckCircle, CircleNotch, Clock, Play, Question, Trophy, X } from '@phosphor-icons/react';
import { usePoker, useIniciarPoker, useVotarPoker, useCerrarPoker } from '@/hooks/usePoker';
import { getCurrentUserEmail } from '@/auth/getCurrentUser';
import { useCatalogs } from '@/hooks/useTasks';
import { fmtDate } from '@/lib/utils';

// Sub-tab "Poker" del TaskModal. Maneja 3 estados visuales:
//   1) No hay votacion -> boton "Iniciar votacion" (escala fibonacci default)
//   2) Hay abierta -> cartas para votar + lista de votantes con check
//      (privados hasta el cierre, solo veo mi propio voto) + boton "Cerrar"
//   3) Hay cerrada -> resumen con mediana + votos de cada uno + boton
//      "Re-estimar" (inicia nueva votacion)
//
// Decisiones de UX para F5 (in-house simple):
//   - Sin tiempo real. Boton "Actualizar" hace refetch manual.
//   - Privacidad: el server oculta los valores de OTROS votos mientras
//     esta abierta. Frontend solo pinta check verde (voto) o gris (no votado).
//   - Cerrar -> calcula mediana -> escribe HorasEstimadas en la tarea.
//     Confirm dialog antes de cerrar muestra el preview de la mediana.
type Props = {
  taskId: number;
  /** Se llama al cerrar la votación con la mediana calculada, para que el
   *  TaskModal sincronice su campo local "Horas estimadas" (el cierre ya la
   *  persiste en la BD, pero el form del modal tiene estado local). */
  onMedianaAplicada?: (horas: number) => void;
};

export function PokerPanel({ taskId, onMedianaAplicada }: Props) {
  const { data: votacion, isLoading, refetch, isFetching } = usePoker(taskId);
  const iniciarMut = useIniciarPoker();
  const votarMut = useVotarPoker();
  const cerrarMut = useCerrarPoker();
  const { data: catalogs } = useCatalogs();

  // Resolvemos el IDCol del usuario actual contra catalogs.users por email
  // (mismo patron que el modal usa para Observacion). Necesario para
  // marcar "mi voto" y decidir el set de votantes esperado.
  const currentIdCol = useMemo(() => {
    if (!catalogs) return null;
    const email = getCurrentUserEmail();
    if (!email) return null;
    return catalogs.users.find((u) => u.correo?.toLowerCase() === email.toLowerCase())?.idCol ?? null;
  }, [catalogs]);

  // Mi voto en la votacion abierta (si voto). Cuando esta cerrada todos
  // los votos son visibles; cuando esta abierta solo el mio (los demas
  // vienen oculto=true).
  const miVoto = useMemo(() => {
    if (!votacion || currentIdCol === null) return null;
    return votacion.votos.find((v) => v.idCol === currentIdCol) ?? null;
  }, [votacion, currentIdCol]);

  if (isLoading) {
    return <div className="text-stone-500 text-sm italic p-4">Cargando…</div>;
  }

  // CASO 1: no hay votacion -> iniciar
  if (!votacion) {
    return (
      <div className="flex flex-col items-center justify-center text-center py-8 gap-3">
        <Trophy size={40} weight="bold" className="text-stone-300" />
        <p className="text-sm text-stone-600 max-w-sm">
          Estimá esta tarea en horas con tu equipo. Cada quien vota a ciegas
          (escala potencias de 2) y al cerrar se aplica la mediana.
        </p>
        <button
          type="button"
          onClick={() => iniciarMut.mutate({ taskId })}
          disabled={iniciarMut.isPending}
          className="px-4 py-2 text-sm bg-brand-green-100 text-black font-semibold rounded hover:bg-brand-green-200 flex items-center gap-1.5 disabled:opacity-60"
        >
          {iniciarMut.isPending ? (
            <><CircleNotch size={14} weight="bold" className="animate-spin" /> Iniciando…</>
          ) : (
            <><Play size={14} weight="bold" /> Iniciar votación</>
          )}
        </button>
      </div>
    );
  }

  const isAbierta = votacion.estado === 'Abierta';

  return (
    <div className="space-y-4">
      {/* Header con metadata + acciones */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs text-stone-600 space-y-0.5">
          <div>
            Iniciada por <strong className="text-stone-800">{votacion.nomIniciador || '—'}</strong>{' '}
            <span className="text-stone-400">· {fmtDate(votacion.fechaApertura, true)}</span>
          </div>
          {votacion.estado === 'Cerrada' && (
            <div>
              Cerrada por <strong className="text-stone-800">{votacion.nomCerradoPor || '—'}</strong>{' '}
              <span className="text-stone-400">· {fmtDate(votacion.fechaCierre, true)}</span>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          title="Actualizar"
          className="p-1.5 rounded hover:bg-stone-100 text-brand-gray-400 hover:text-brand-gray-500 disabled:opacity-50"
        >
          <ArrowsClockwise size={14} weight="bold" className={isFetching ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Resultado al cerrar — banner verde con la mediana */}
      {votacion.estado === 'Cerrada' && votacion.valorFinal != null && (
        <div className="bg-emerald-50 border-l-4 border-emerald-500 rounded-r-md px-4 py-3 flex items-center gap-3">
          <Trophy size={20} weight="bold" className="text-emerald-700 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-emerald-900">
              Mediana: <span className="font-mono">{votacion.valorFinal.toFixed(2)}h</span>
            </p>
            <p className="text-xs text-emerald-800/80">
              Se aplicó a Horas estimadas de la tarea.
            </p>
          </div>
        </div>
      )}

      {/* Cartas para votar — solo cuando esta abierta */}
      {isAbierta && (
        <PokerCards
          escala={votacion.escalaValores}
          miValor={miVoto?.valor ?? null}
          esMiInterrogante={miVoto?.esInterrogante ?? false}
          disabled={votarMut.isPending}
          onVote={(valor, esInterrogante) =>
            votarMut.mutate({ taskId, valor, esInterrogante })
          }
        />
      )}

      {/* Lista de votos */}
      <VotosList
        votos={votacion.votos}
        estado={votacion.estado}
      />

      {/* Cerrar — solo cuando abierta y hay al menos un voto numerico */}
      {isAbierta && (
        <CerrarButton
          taskId={taskId}
          votos={votacion.votos}
          isPending={cerrarMut.isPending}
          onCerrar={() =>
            cerrarMut.mutate(taskId, {
              onSuccess: (data) => {
                if (data?.valorFinal != null) onMedianaAplicada?.(data.valorFinal);
              },
            })
          }
        />
      )}

      {/* Re-estimar — solo cuando cerrada */}
      {votacion.estado === 'Cerrada' && (
        <div className="border-t border-stone-200 pt-3 flex justify-center">
          <button
            type="button"
            onClick={() => iniciarMut.mutate({ taskId })}
            disabled={iniciarMut.isPending}
            className="px-3 py-1.5 text-xs border border-stone-300 rounded hover:bg-stone-50 flex items-center gap-1.5 disabled:opacity-50"
          >
            <Play size={12} weight="bold" /> Re-estimar (nueva votación)
          </button>
        </div>
      )}
    </div>
  );
}

// Grid de cartas. Click vota inmediatamente (upsert en server). La carta
// con "?" emite (null, true); las numericas emiten (valor, false).
function PokerCards({
  escala, miValor, esMiInterrogante, disabled, onVote,
}: {
  escala: number[];
  miValor: number | null;
  esMiInterrogante: boolean;
  disabled: boolean;
  onVote: (valor: number | null, esInterrogante: boolean) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {escala.map((v) => {
        const selected = !esMiInterrogante && miValor === v;
        return (
          <button
            key={v}
            type="button"
            onClick={() => onVote(v, false)}
            disabled={disabled}
            className={`min-w-[3rem] h-14 px-2 rounded-md border-2 font-mono text-base font-bold transition-all
              ${selected
                ? 'bg-brand-green-100 border-brand-green-200 text-black scale-105'
                : 'bg-white border-stone-300 text-stone-700 hover:border-brand-green-100 hover:bg-stone-50'}
              disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {v}
          </button>
        );
      })}
      <button
        key="?"
        type="button"
        onClick={() => onVote(null, true)}
        disabled={disabled}
        title="No tengo contexto suficiente para estimar"
        className={`min-w-[3rem] h-14 px-2 rounded-md border-2 font-bold transition-all flex items-center justify-center
          ${esMiInterrogante
            ? 'bg-amber-100 border-amber-400 text-amber-900 scale-105'
            : 'bg-white border-stone-300 text-stone-500 hover:border-amber-300 hover:bg-amber-50'}
          disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        <Question size={20} weight="bold" />
      </button>
    </div>
  );
}

// Lista de votantes. En Abierta solo muestra check verde (voto) o ?
// (interrogante). En Cerrada revela el valor.
function VotosList({
  votos, estado,
}: {
  votos: ReadonlyArray<{
    idCol: number;
    nombreCompleto?: string | null;
    valor: number | null;
    esInterrogante: boolean;
    oculto: boolean;
  }>;
  estado: string;
}) {
  if (votos.length === 0) {
    return (
      <p className="text-xs text-stone-500 italic">
        Sin votos todavía. Apretá una carta para votar.
      </p>
    );
  }
  return (
    <div className="border-t border-stone-200 pt-3 space-y-1.5">
      <h4 className="text-xs font-semibold text-stone-700 uppercase tracking-wide">
        Votos ({votos.length})
      </h4>
      {votos.map((v) => (
        <div key={v.idCol} className="flex items-center justify-between text-sm">
          <span className="text-stone-800 truncate">{v.nombreCompleto || `#${v.idCol}`}</span>
          <span>
            {v.oculto ? (
              <span title="Voto oculto hasta cierre" className="text-emerald-600">
                <CheckCircle size={16} weight="bold" />
              </span>
            ) : v.esInterrogante ? (
              <span className="text-amber-700 font-bold flex items-center gap-1">
                <Question size={14} weight="bold" /> ?
              </span>
            ) : (
              <span className="font-mono font-semibold text-stone-900">
                {v.valor != null ? v.valor.toFixed(v.valor % 1 === 0 ? 0 : 2) : '—'}h
              </span>
            )}
          </span>
        </div>
      ))}
      {estado === 'Abierta' && votos.some((v) => v.oculto) && (
        <p className="text-[10px] text-stone-400 italic pt-1">
          Los valores se revelan al cerrar la votación.
        </p>
      )}
    </div>
  );
}

// Boton Cerrar con confirm que muestra el preview de la mediana. Calcula
// la mediana local-side igual que el backend (para mostrarla en el confirm).
// Si todos los votos son "?" no se puede cerrar (mensaje claro).
function CerrarButton({
  taskId: _taskId, votos, isPending, onCerrar,
}: {
  taskId: number;
  votos: ReadonlyArray<{ valor: number | null; esInterrogante: boolean; oculto: boolean }>;
  isPending: boolean;
  onCerrar: () => void;
}) {
  const [showConfirm, setShowConfirm] = useState(false);

  // Para calcular el preview localmente necesitamos saber cuantos votos
  // numericos hay. Mientras esta abierta el server oculta el valor pero
  // EsInterrogante de cada uno tambien (porque revelarlo seria pista). Asi
  // que aca, mientras abierta, NO podemos calcular la mediana local — el
  // server la calcula al cerrar. Mostramos solo el count de votos totales.
  const totalVotos = votos.length;

  function handleClick() {
    if (totalVotos === 0) {
      alert('No hay votos para cerrar la votación.');
      return;
    }
    setShowConfirm(true);
  }

  return (
    <>
      <div className="border-t border-stone-200 pt-3 flex justify-end">
        <button
          type="button"
          onClick={handleClick}
          disabled={isPending || totalVotos === 0}
          title={totalVotos === 0 ? 'Necesita al menos un voto' : 'Calcular mediana y aplicar a Horas estimadas'}
          className="px-3 py-1.5 text-xs bg-brand-green-100 text-black font-semibold rounded hover:bg-brand-green-200 flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isPending ? (
            <><CircleNotch size={12} weight="bold" className="animate-spin" /> Cerrando…</>
          ) : (
            <><Clock size={12} weight="bold" /> Cerrar y aplicar mediana</>
          )}
        </button>
      </div>

      {showConfirm && (
        <div
          className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4"
          onClick={() => setShowConfirm(false)}
        >
          <div
            className="bg-white rounded-lg w-full max-w-sm p-5 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold">Cerrar votación</h3>
              <button
                onClick={() => setShowConfirm(false)}
                className="p-1 hover:bg-stone-100 rounded"
              >
                <X size={16} weight="bold" />
              </button>
            </div>
            <p className="text-sm text-stone-700">
              Se va a calcular la mediana de los votos numéricos (ignorando los <strong>?</strong>) y
              escribir el resultado en <strong>Horas estimadas</strong> de la tarea. La votación
              quedará archivada como histórico.
            </p>
            <p className="text-xs text-stone-500">
              {totalVotos} voto{totalVotos !== 1 ? 's' : ''} registrado{totalVotos !== 1 ? 's' : ''}.
              El valor exacto lo verás al cerrar.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setShowConfirm(false)}
                className="px-3 py-1.5 text-sm border border-stone-300 rounded hover:bg-stone-50"
              >
                Cancelar
              </button>
              <button
                onClick={() => { setShowConfirm(false); onCerrar(); }}
                className="px-4 py-1.5 text-sm bg-brand-green-100 text-black font-semibold rounded hover:bg-brand-green-200"
              >
                Cerrar y aplicar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
